import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLiveSession } from '@/hooks/useLiveSession';
import { usePlaylist } from '@/hooks/usePlaylist';
import { useTracks } from '@/hooks/useTracks';
import { YouTubePlayer } from '@/components/player/YouTubePlayer';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { LiveSessionService } from '@/services/liveSession.service';
import { AutoMixService } from '@/services/autoMix.service';
import { toast } from 'react-toastify';

export function LiveSessionPage() {
    const { id: playlistId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { playlist } = usePlaylist(playlistId);
    const { tracks } = useTracks(playlistId);

    const [sessionId, setSessionId] = useState<string | null>(null);
    const [initLoading, setInitLoading] = useState(true);

    // Step 1: Find the active session for this playlist
    useEffect(() => {
        if (!playlistId) return;

        LiveSessionService.getSessionByPlaylist(playlistId)
            .then(session => {
                if (session) {
                    setSessionId(session.id);
                } else {
                    // If no active session found, we stay on this page but show "No Active Session"
                }
            })
            .catch(console.error)
            .finally(() => setInitLoading(false));
    }, [playlistId]);

    // Step 2: Subscribe to session
    const {
        session,
        isLoading: sessionLoading,
        joinSession,
        updateState,
        endSession,
        isAdmin
    } = useLiveSession(sessionId || undefined);

    // Auto-join on enter
    useEffect(() => {
        if (sessionId && user && session) {
            // If not in connectedUsers, join
            const isConnected = session.connectedUsers.includes(user.id);
            if (!isConnected) {
                joinSession(sessionId);
            }
        }
        return () => {
            if (sessionId && user) {
                // optional cleanup
            }
        };
    }, [sessionId, user?.id, session?.connectedUsers]);

    // Logic for next track
    const handleVideoEnd = () => {
        if (!isAdmin || !session || !tracks.length) return;

        // Find current index
        let nextIndex = session.currentVideoIndex + 1;
        if (nextIndex >= tracks.length) {
            // End of playlist? 
            // In Infinite Mode, we expect a new track to have arrived.
            // If it hasn't arrived (API fail?), we should loop or stop.
            // Requirement: "if the last track is manually removed, then proceed to stop".
            // If we are here, it means nextIndex is out of bounds.
            // Let's loop for now as a safe fallback to keep music playing, 
            // or maybe just do nothing and let the player stop?
            // "Play next" implies we have a next.
            // Let's loop to start (0) if we really ran out.
            nextIndex = 0;

            // OPTIONAL: If we want strict "Stop if removed", we could check if auto-add failed.
            // But looping 0 is safer for a "Party" app.
        }

        const nextTrack = tracks[nextIndex];
        if (nextTrack) {
            updateState({
                currentVideoId: nextTrack.videoId,
                currentVideoIndex: nextIndex,
                currentTime: 0,
                isPlaying: true
            });
        }
    };

    // Manual Start Playback Handler
    const handleStartPlayback = async () => {
        if (tracks.length > 0) {
            console.log('Starting playback with track:', tracks[0]);
            try {
                if (!tracks[0].videoId) {
                    throw new Error('Track has no video ID');
                }

                await updateState({
                    currentVideoId: tracks[0].videoId,
                    currentVideoIndex: 0,
                    currentTime: 0,
                    isPlaying: true
                });
                console.log('Playback state updated successfully');
            } catch (err) {
                console.error('Failed to start playback:', err);
                alert('Failed to start playback. Check console for details. Error: ' + (err as Error).message);
            }
        } else {
            console.warn('No tracks available to play');
        }
    };

    // Auto-DJ Logic: Trigger when last track starts
    // Use a ref to track the last processed video ID to preventing duplicate calls for the same track
    const lastProcessedVideoIdRef = useRef<string | null>(null);
    const [autoMixProcessing, setAutoMixProcessing] = useState(false);

    useEffect(() => {
        if (!isAdmin || !session || !tracks.length) {
            // console.log('AutoMix: Waiting for session/tracks...', { isAdmin, hasSession: !!session, trackCount: tracks.length });
            return;
        }

        const isLastTrack = session.currentVideoIndex === tracks.length - 1;
        const currentTrack = tracks[session.currentVideoIndex];

        // Debug Log
        // console.log('AutoMix Check:', {
        //    index: session.currentVideoIndex, 
        //    total: tracks.length, 
        //    isLast: isLastTrack,
        //    currentVid: session.currentVideoId,
        //    trackVid: currentTrack?.videoId,
        //    processing: autoMixProcessing,
        //    lastProcessed: lastProcessedVideoIdRef.current
        // });

        if (isLastTrack && currentTrack && !autoMixProcessing) {
            // Check if we already processed this specific video instance
            if (lastProcessedVideoIdRef.current === currentTrack.videoId) {
                return;
            }

            console.log('AutoMix: Last track detected. Initiating generation...');
            lastProcessedVideoIdRef.current = currentTrack.videoId;
            setAutoMixProcessing(true);

            toast.info(`Auto-DJ: Finding a song similar to "${currentTrack.title}"...`, { autoClose: 3000 });

            AutoMixService.generateNextTrack(playlistId!, tracks, currentTrack, user!.id)
                .then(success => {
                    if (success) {
                        toast.success('Auto-DJ added a track!');
                    } else {
                        toast.warning('Auto-DJ could not find a suitable track.');
                        // If failed, do we clear the ref so it retries? 
                        // Probably not immediately, or we spam.
                        // Better to let the user manually add one or replay.
                    }
                })
                .catch(err => {
                    console.error('AutoMix Error:', err);
                    toast.error('Auto-DJ failed.');
                })
                .finally(() => {
                    setAutoMixProcessing(false);
                });
        }
    }, [isAdmin, session?.currentVideoIndex, session?.currentVideoId, tracks.length]);


    // Initial Play (if session has no video selected yet)
    useEffect(() => {
        // Only auto-play if explicitly not playing anything yet
        if (isAdmin && session && !session.currentVideoId && tracks.length > 0) {
            handleStartPlayback();
        }
    }, [isAdmin, session?.id, tracks.length]);


    if (initLoading || sessionLoading) return <LoadingSpinner fullScreen />;
    if (!session) return (
        <div className="container py-5 text-center">
            <h3>No Live Session Active</h3>
            <button className="btn btn-primary mt-3" onClick={() => navigate(`/playlists/${playlistId}`)}>
                Back to Playlist
            </button>
        </div>
    );

    const currentTrack = tracks.find(t => t.videoId === session.currentVideoId);

    return (
        <div className="container-fluid vh-100 d-flex flex-column overflow-hidden bg-dark text-white p-0">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom border-secondary bg-darker">
                <div className="d-flex align-items-center gap-3">
                    <span className="badge bg-danger animate-pulse">● LIVE</span>
                    <h5 className="mb-0">{playlist?.name}</h5>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <span><i className="bi bi-eye"></i> {session.connectedUsers.length}</span>
                    <button className="btn btn-outline-light btn-sm" onClick={() => navigate(`/playlists/${playlistId}`)}>
                        Exit
                    </button>
                    {isAdmin && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                            await endSession();
                            navigate(`/playlists/${playlistId}`);
                        }}>
                            End Session
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-grow-1 d-flex overflow-hidden">
                {/* Main Content: Player */}
                <div className="flex-grow-1 d-flex flex-column bg-black justify-content-center p-4">
                    {session.currentVideoId ? (
                        <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', height: '70vh' }}>
                            <YouTubePlayer
                                videoId={session.currentVideoId}
                                session={session}
                                isAdmin={isAdmin}
                                onStateUpdate={updateState}
                                onVideoEnd={handleVideoEnd}
                            />
                            {currentTrack && (
                                <div className="mt-3 text-center">
                                    <h3>{currentTrack.title}</h3>
                                    <p className="text-secondary">{currentTrack.artist}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center text-secondary d-flex flex-column align-items-center justify-content-center h-100">
                            <h3 className="mb-3">Waiting for tracks...</h3>
                            {isAdmin && (
                                <div className="d-flex gap-3">
                                    {tracks.length > 0 && (
                                        <button className="btn btn-primary btn-lg rounded-pill" onClick={handleStartPlayback}>
                                            <i className="bi bi-play-fill me-2"></i> Start Playback
                                        </button>
                                    )}
                                    {tracks.length > 0 && (
                                        <button
                                            className="btn btn-outline-info btn-lg rounded-pill"
                                            onClick={() => {
                                                const lastTrack = tracks[tracks.length - 1];
                                                if (!lastTrack) return;
                                                toast.info('Manual Auto-DJ Triggered...');
                                                AutoMixService.generateNextTrack(playlistId!, tracks, lastTrack, user!.id)
                                                    .then(res => {
                                                        if (res) toast.success('Track added!');
                                                        else toast.warning('No track found');
                                                    });
                                            }}
                                        >
                                            <i className="bi bi-magic me-2"></i> Auto-DJ Now
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Admin Controls below player */}
                    {isAdmin && session.currentVideoId && (
                        <div className="d-flex justify-content-center gap-3 mt-3">
                            <button
                                className="btn btn-outline-light rounded-pill px-4"
                                onClick={handleVideoEnd}
                                title="Skip to Next"
                            >
                                <i className="bi bi-skip-end-fill me-2"></i> Next Track
                            </button>

                            <button
                                className="btn btn-outline-info rounded-pill px-4"
                                disabled={autoMixProcessing}
                                onClick={() => {
                                    const lastTrack = tracks[tracks.length - 1];
                                    if (!lastTrack) return;
                                    toast.info('Manual Auto-DJ Triggered...');
                                    AutoMixService.generateNextTrack(playlistId!, tracks, lastTrack, user!.id)
                                        .then(res => {
                                            if (res) toast.success('Track added!');
                                            else toast.warning('No track found');
                                        });
                                }}
                                title="Force Auto-DJ to add a track"
                            >
                                <i className="bi bi-magic me-2"></i> Suggest Next
                            </button>
                        </div>
                    )}
                </div>

                {/* Sidebar: Queue & Chat */}
                <div className="d-flex flex-column border-start border-secondary" style={{ width: '350px' }}>
                    {/* Queue (Top Half - Simplified) */}
                    <div className="flex-grow-1 overflow-auto border-bottom border-secondary bg-darker p-3" style={{ height: '40%' }}>
                        <h6 className="text-light mb-3">Up Next</h6>
                        {tracks.map((track, idx) => (
                            <div
                                key={track.id}
                                className={`d-flex gap-2 align-items-center p-2 rounded mb-2 ${track.videoId === session.currentVideoId ? 'bg-primary text-white' : 'bg-secondary bg-opacity-25'}`}
                            >
                                <img src={track.thumbnail} className="rounded" width="40" height="30" style={{ objectFit: 'cover' }} />
                                <div className="text-truncate small flex-grow-1">
                                    <div className="fw-bold text-truncate">{track.title}</div>
                                    <div className="opacity-75 text-truncate">{track.artist}</div>
                                </div>
                                {idx === session.currentVideoIndex && <i className="bi bi-play-fill text-white"></i>}
                            </div>
                        ))}
                    </div>

                    {/* Chat (Bottom Half) */}
                    <div className="flex-grow-1" style={{ height: '60%' }}>
                        <ChatPanel playlistId={playlistId!} />
                    </div>
                </div>
            </div>
        </div>
    );
}
